// Components
import BaseModal from '../components/common/elements/BaseModal.vue'
import CheckboxInput from '../components/common/elements/CheckboxInput.vue'
import DropdownInput from '../components/common/elements/DropdownInput.vue'
import ErrorFeedback from '../components/common/elements/ErrorFeedback.vue'
import PhoneNumber from '../components/common/elements/PhoneNumber.vue'
import RadioGroup from '../components/common/elements/RadioGroup.vue'
import RadioInput from '../components/common/elements/RadioInput.vue'
import TextArea from '../components/common/elements/TextArea.vue'
import TextInput from '../components/common/elements/TextInput.vue'

// Libraries
import Vue from 'vue'
import moment from 'moment'

// Services
import store from '../store'
import router from '../router'

// Utilities
import analytics from '../utility/analyticsUtil'
import isEmpty from '../utility/isEmpty'
import logger from '../utility/loggerUtil'
import messaging from '../utility/messagingUtil'
import sessionRecord from '../utility/sessionRecordUtil'
import directives from '../directives/index.js'
import { RegexConstants } from '../constants.js'

export default class StartupService {
  static async init () {
    this.initPolyfills()
    this.initGlobalComponents()
    await this.initServiceWorker()
    this.initDeviceInfo()
    this.initGlobalFilters()
    this.initDirectives()
    this.initGlobalRouteGuards()

    await this.getConfigSettings()
    await StartupService.initUserDataCollection()
  }

  static async initUserDataCollection () {
    logger.debug('Initializing user data collection services...')

    await StartupService.initAnalytics()
    await StartupService.initMessaging()
  }

  static async initMessaging () {
    await messaging.init()
  }

  static initPolyfills () {
    if (!Array.prototype.includes) {
      Object.defineProperty(Array.prototype, 'includes', { // eslint-disable-line
        value: function (searchElement, fromIndex) {
          if (this == null) {
            throw new TypeError('"this" is null or not defined')
          }

          // 1. Let O be ? ToObject(this value).
          const o = Object(this)

          // 2. Let len be ? ToLength(? Get(O, "length")).
          const len = o.length >>> 0

          // 3. If len is 0, return false.
          if (len === 0) {
            return false
          }

          // 4. Let n be ? ToInteger(fromIndex).
          //    (If fromIndex is undefined, this step produces the value 0.)
          const n = fromIndex | 0

          // 5. If n ≥ 0, then
          //  a. Let k be n.
          // 6. Else n < 0,
          //  a. Let k be len + n.
          //  b. If k < 0, let k be 0.
          let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0)

          function sameValueZero (x, y) {
            return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y))
          }

          // 7. Repeat, while k < len
          while (k < len) {
            // a. Let elementK be the result of ? Get(O, ! ToString(k)).
            // b. If SameValueZero(searchElement, elementK) is true, return true.
            if (sameValueZero(o[k], searchElement)) {
              return true
            }
            // c. Increase k by 1.
            k++
          }

          // 8. Return false
          return false
        }
      })
    }
  }

  static async initAnalytics () {
    await analytics.init()

    analytics.saveInitUserProperties()
    sessionRecord.saveInitUserProperties()
  }

  static initGlobalFilters () {
    Vue.filter('dollar', (value) => {
      if (isEmpty(value)) {
        return value
      }
      // TODO: Find a better regex to do the full formatting. The sign thing is just overcomplicated but quick
      const currencyValue = parseFloat(value).toFixed(0)
      return (currencyValue.indexOf('-') === 0 ? '-' : '') + '$' + currencyValue.replace('-', '')
    })

    Vue.filter('currency', function (value) {
      if (isEmpty(value)) {
        return value
      }
      // TODO: Find a better regex to do the full formatting. The sign thing is just overcomplicated but quick
      const currencyValue = parseFloat(value).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,')
      return (currencyValue.indexOf('-') === 0 ? '-' : '') + '$' + currencyValue.replace('-', '')
    })

    /**
     * Returns a formatted date string from the passed in moment object.
     * @param {moment} [value] - The date to format.
     * @returns {String} A formatted date string in the form: MMMM, D, YYYY (example: May 9, 2019).
     */
    Vue.filter('fullShortDate', function (value) { // value should be a moment object
      if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
        return ''
      }

      if (value.month() === 5 || value.month() === 6) {
        return value.format('MMMM D, YYYY')
      }

      return value.format('MMM D, YYYY')
    })

    Vue.filter('phoneDash', function (value) {
      if (!value || value.length < 10) {
        return value
      }

      value = value.replace(RegexConstants.phoneNumberFormatting, '')
      value = value.replace('+1', '')
      value = `${value.substring(0, 3)}-${value.substring(3, 6)}-${value.substring(6)}`

      return value
    })

    Vue.filter('shortMonthDayDate', function (value) {
      if (!value || value <= new Date(1901, 1, 1)) {
        return ''
      }

      return moment(value).format('MMM D')
    })

    Vue.filter('fullMonthDayYear', function (value) {
      if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
        return ''
      }

      return moment(value).format('MMM D, YYYY')
    })

    Vue.filter('standardMonthDayYear', function (value) {
      if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
        return ''
      }

      const momentValue = moment(value)
      const showFullMonth = momentValue.month() === 4 || momentValue.month() === 5 || momentValue.month() === 6 // May, June, July
      return momentValue.format(showFullMonth ? 'MMMM DD[,] YYYY' : 'MMM[.] DD[,] YYYY')
    })

    Vue.filter('dayNameMonthDayYear', function (value) {
      if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
        return ''
      }

      const momentValue = moment(value)
      const showFullMonth = momentValue.month() === 4 || momentValue.month() === 5 || momentValue.month() === 6 // May, June, July
      return momentValue.format(showFullMonth ? 'dddd, MMMM DD[,] YYYY' : 'dddd, MMM[.] DD[,] YYYY')
    })

    Vue.filter('shortDashDate', function (value) {
      if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
        return ''
      }

      return value.format('MM-DD-YYYY')
    })

    Vue.filter('uppercase', (value) => {
      return (value || value === 0) ? value.toString().toUpperCase() : ''
    })

    Vue.filter('sentenceCase', (value) => {
      if (!value && value !== 0) {
        return ''
      }

      value = value.toString().toLowerCase()

      const valParts = value.split(' ')

      const sentenceCaseVal = []

      for (let i = 0; i < valParts.length; i++) {
        sentenceCaseVal.push(valParts[i].charAt(0).toUpperCase() + valParts[i].slice(1))
      }

      return sentenceCaseVal.join(' ')
    })

    Vue.filter('lowercase', (value) => {
      return (value || value === 0) ? value.toString().toLowerCase() : ''
    })
  }

  static initDirectives () {
    // Formats a currency input to 2 decimal precision
    // TODO: Fix jerky input when typing in middle or before $
    Vue.directive('currency', directives.CurrencyDirective)

    // Auto focuses the element on page load
    Vue.directive('focus', directives.FocusDirective)

    // Formats input to currency and prevents non-numeric inputs
    Vue.directive('inputCurrency', directives.InputCurrencyDirective)

    Vue.directive('masked', directives.MaskedDirective)

    // Prevents non-numeric inputs
    Vue.directive('numeric', directives.NumericDirective)

    Vue.directive('uppercase', directives.UppercaseDirective)
  }

  static initDeviceInfo () {
    store.commit('display/setDeviceBrowserInfo')

    if (process.env.NODE_ENV === 'testing') { // Allow changes for unit testing
      window.isMobileDevice = false
    } else {
      Object.defineProperty(window, 'isMobileDevice', {
        value: false,
        configurable: false,
        writable: false
      })
    }
  }

  static async getConfigSettings () {
    try {
      await store.dispatch('session/getConfigSettings')
    } catch (e) {
      logger.error('Error attempting to get config settings')
      logger.debug(e)
    }
  }

  static initGlobalComponents () {
    Vue.component('base-modal', BaseModal)
    Vue.component('checkbox-input', CheckboxInput)
    Vue.component('dropdown-input', DropdownInput)
    Vue.component('error-feedback', ErrorFeedback)
    Vue.component('phone-number', PhoneNumber)
    Vue.component('radio-group', RadioGroup)
    Vue.component('radio-input', RadioInput)
    Vue.component('text-area', TextArea)
    Vue.component('text-input', TextInput)
  }

  /**
   * Initializes route guards to prevent or redirect navigation depending on defined conditions.
   */
  static initGlobalRouteGuards () {
    const navigationAllowed = (to, from) => { // Checks if navigation is allowed from one specific route to another
      if (!isEmpty(from.meta) && !isEmpty(from.meta.preventNavigation) && from.meta.preventNavigation.indexOf(to.name) > -1) {
        return false
      }

      return true
    }

    router.beforeEach((to, from, next) => {
      if (navigationAllowed(to, from) === false) { // Create more functions and add them here to alter navigation with different conditions
        return next(false)
      }

      return next()
    })
  }

  /**
   * Registers a new service worker, required for PWA config.
   */
  static async initServiceWorker () {
    if (!('serviceWorker' in navigator)) {
      logger.debug('ServiceWorker not supported')
      return
    }

    try {
      const registration = await navigator.serviceWorker.register('/serviceWorker.js')

      logger.debug('ServiceWorker registration successful with scope: ' + registration.scope)
    } catch (e) {
      logger.error('Error registering service worker')
      logger.debug(e)
    }
  }
}
